import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { HitApi } from '../../Api/ApiHit'
import { addAppBanner, searchAppBanner, deleteAppBanner, updateAppBanner, getAppConfig, updateAppConfig, searchConfigProject } from '../../constant/Constant'
import { getSelectedUnit } from '../../storage/Storage'
import { setApiJson } from '../../redux/Actions/ApiAction'
import FileuploadComp from '../../components/SingleFileUpload/FileuploadComp'
import toast from 'react-hot-toast'
import {
    Loader2, Plus, Trash2, X, Image,
    Settings, Pencil, FolderOpen, ChevronDown
} from 'lucide-react'
import SectionsManager from './components/SectionsManager'
import AddProjectModal from './components/AddProjectModal'

const ClientWebApp = () => {
    const dispatch = useDispatch()
    const ApiReducer = useSelector(state => state.ApiReducer)

    const [activeTab, setActiveTab] = useState('banners')
    const [banners, setBanners] = useState([])
    const [bannersLoading, setBannersLoading] = useState(false)
    const [showAddBannerModal, setShowAddBannerModal] = useState(false)
    const [showEditBannerModal, setShowEditBannerModal] = useState(false)
    const [editingBanner, setEditingBanner] = useState(null)
    const [isBannerSubmitting, setIsBannerSubmitting] = useState(false)

    // App Configuration State
    const [appConfig, setAppConfig] = useState(null)
    const [configLoading, setConfigLoading] = useState(false)
    const [isSavingConfig, setIsSavingConfig] = useState(false)
    const [draftConfig, setDraftConfig] = useState(null)

    // Project State
    const [projects, setProjects] = useState([])
    const [selectedProject, setSelectedProject] = useState(null)
    const [projectsLoading, setProjectsLoading] = useState(false)
    const [showAddProjectModal, setShowAddProjectModal] = useState(false)

    const unitId = getSelectedUnit()?._id || getSelectedUnit()?.[0]

    useEffect(() => {
        fetchProjects()
    }, [unitId])

    useEffect(() => {
        if (selectedProject) {
            fetchBanners()
        } else {
            setBanners([])
        }
    }, [unitId, selectedProject])

    useEffect(() => {
        if (activeTab === 'settings' && selectedProject) {
            fetchAppConfig()
        } else if (!selectedProject) {
            setAppConfig(null)
        }
    }, [activeTab, unitId, selectedProject])

    const fetchProjects = async () => {
        if (!unitId) return
        setProjectsLoading(true)
        try {
            const json = {
                page: 1,
                limit: 100,
                search: { unitIds: unitId, isActive: true },
                sort: { createdAt: -1 }
            }
            const res = await HitApi(json, searchConfigProject)
            const projectList = res?.data?.docs || []
            setProjects(projectList)
            // Auto-select first project if available and none selected
            if (projectList.length > 0 && !selectedProject) {
                setSelectedProject(projectList[0])
            } else if (projectList.length === 0) {
                setSelectedProject(null)
            }
        } catch (error) {
            setProjects([])
            setSelectedProject(null)
        }
        setProjectsLoading(false)
    }

    const handleProjectChange = (projectId) => {
        const project = projects.find(p => p._id === projectId)
        setSelectedProject(project || null)
    }

    const handleProjectCreated = (newProject) => {
        setProjects(prev => [newProject, ...prev])
        setSelectedProject(newProject)
    }

    const fetchBanners = () => {
        if (!unitId || !selectedProject) return
        setBannersLoading(true)
        const json = { page: 1, limit: 100, search: { unitIds: unitId, projectId: selectedProject._id, isActive: true }, sort: { order: 1 } }
        HitApi(json, searchAppBanner).then((res) => {
            setBanners(res?.data?.docs || [])
            setBannersLoading(false)
        }).catch(() => {
            setBanners([])
            setBannersLoading(false)
        })
    }

    const handleAddBanner = () => {
        dispatch(setApiJson({ title: '', order: 0, gender: 'all' }))
        setShowAddBannerModal(true)
    }

    const handleBannerSubmit = (e) => {
        e.preventDefault()
        const imageUrl = ApiReducer?.apiJson?.imageUrl
        const title = ApiReducer?.apiJson?.title
        const order = ApiReducer?.apiJson?.order
        const bannerGender = ApiReducer?.apiJson?.gender || 'all'

        if (!title) { toast.error('Please enter a title'); return }
        if (!imageUrl) { toast.error('Please upload an image'); return }

        setIsBannerSubmitting(true)
        const json = { unitIds: unitId, projectId: selectedProject._id, title, imageUrl, order: parseInt(order) || 0, gender: bannerGender, isActive: true }

        HitApi(json, addAppBanner).then((res) => {
            if (res?.success) {
                toast.success('Banner added successfully')
                setShowAddBannerModal(false)
                dispatch(setApiJson(null))
                fetchBanners()
            } else {
                toast.error(res?.message || 'Failed to add banner')
            }
            setIsBannerSubmitting(false)
        }).catch(() => {
            toast.error('Failed to add banner')
            setIsBannerSubmitting(false)
        })
    }

    const handleDeleteBanner = (banner) => {
        if (!window.confirm(`Delete "${banner.title}"?`)) return
        HitApi({ _id: banner._id }, deleteAppBanner).then((res) => {
            if (res?.success) {
                toast.success('Banner deleted')
                fetchBanners()
            } else {
                toast.error('Failed to delete banner')
            }
        }).catch(() => toast.error('Failed to delete banner'))
    }

    const handleEditBanner = (banner) => {
        setEditingBanner(banner)
        dispatch(setApiJson({
            title: banner.title,
            order: banner.order,
            imageUrl: banner.imageUrl,
            gender: banner.gender || 'all'
        }))
        setShowEditBannerModal(true)
    }

    const handleBannerUpdate = (e) => {
        e.preventDefault()
        const title = ApiReducer?.apiJson?.title
        const order = ApiReducer?.apiJson?.order
        const bannerGender = ApiReducer?.apiJson?.gender || 'all'

        if (!title) { toast.error('Please enter a title'); return }

        setIsBannerSubmitting(true)
        const json = { _id: editingBanner._id, title, order: parseInt(order) || 0, gender: bannerGender }

        HitApi(json, updateAppBanner).then((res) => {
            if (res?.success) {
                toast.success('Banner updated successfully')
                setShowEditBannerModal(false)
                setEditingBanner(null)
                dispatch(setApiJson(null))
                fetchBanners()
            } else {
                toast.error(res?.message || 'Failed to update banner')
            }
            setIsBannerSubmitting(false)
        }).catch(() => {
            toast.error('Failed to update banner')
            setIsBannerSubmitting(false)
        })
    }

    const handleBannerInputChange = (field, value) => {
        const currentJson = ApiReducer?.apiJson || {}
        dispatch(setApiJson({ ...currentJson, [field]: value }))
    }

    // App Configuration Functions
    const fetchAppConfig = () => {
        if (!unitId || !selectedProject) return
        setConfigLoading(true)
        const json = { unitIds: unitId, projectId: selectedProject._id }
        HitApi(json, getAppConfig).then((res) => {
            if (res?.success && res?.data) {
                setAppConfig(res.data)
                setDraftConfig(null)
            } else {
                setAppConfig({ maintenanceMode: { enabled: false, message: '' } })
                setDraftConfig(null)
            }
            setConfigLoading(false)
        }).catch(() => {
            setConfigLoading(false)
            setAppConfig({ maintenanceMode: { enabled: false, message: '' } })
            setDraftConfig(null)
        })
    }

    const handleConfigUpdate = (key, value) => {
        if (!unitId || !selectedProject) return
        setIsSavingConfig(true)
        const updatedConfig = { ...appConfig }
        const keys = key.split('.')
        if (keys.length === 2) {
            updatedConfig[keys[0]] = { ...updatedConfig[keys[0]], [keys[1]]: value }
        } else {
            updatedConfig[key] = value
        }
        setAppConfig(updatedConfig)

        const json = { unitIds: unitId, projectId: selectedProject._id, ...updatedConfig }
        HitApi(json, updateAppConfig).then((res) => {
            if (res?.success) {
                toast.success('Configuration updated successfully')
            } else {
                toast.error(res?.message || 'Failed to update configuration')
                fetchAppConfig()
            }
            setIsSavingConfig(false)
        }).catch(() => {
            toast.error('Failed to update configuration')
            fetchAppConfig()
            setIsSavingConfig(false)
        })
    }

    const handleSaveDraftConfig = () => {
        if (!unitId || !selectedProject || !draftConfig) return
        setIsSavingConfig(true)
        const merged = { ...appConfig, ...draftConfig }
        setAppConfig(merged)

        const json = { unitIds: unitId, projectId: selectedProject._id, ...merged }
        HitApi(json, updateAppConfig).then((res) => {
            if (res?.success) {
                toast.success('Configuration updated successfully')
                setDraftConfig(null)
            } else {
                toast.error(res?.message || 'Failed to update configuration')
                fetchAppConfig()
            }
            setIsSavingConfig(false)
        }).catch(() => {
            toast.error('Failed to update configuration')
            fetchAppConfig()
            setIsSavingConfig(false)
        })
    }

    const getConfigValue = (path) => {
        const merged = draftConfig ? { ...appConfig, ...draftConfig } : appConfig
        const keys = path.split('.')
        let val = merged
        for (const k of keys) {
            val = val?.[k]
        }
        return val
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b px-6 py-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-xl font-semibold text-gray-900">Client Web App</h1>
                        <p className="text-sm text-gray-500 mt-1">Manage banners, sections and app settings</p>
                    </div>
                    <div className="flex items-center gap-4">
                        {/* Project Selector */}
                        <div className="flex items-center gap-2">
                            <FolderOpen className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-500">Project:</span>
                            {projectsLoading ? (
                                <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-lg">
                                    <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                                    <span className="text-sm text-gray-400">Loading...</span>
                                </div>
                            ) : (
                                <div className="relative">
                                    <select
                                        value={selectedProject?._id || ''}
                                        onChange={(e) => handleProjectChange(e.target.value)}
                                        className="appearance-none px-3 py-1.5 pr-8 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none min-w-[180px]"
                                    >
                                        <option value="">Select Project</option>
                                        {projects.map(p => (
                                            <option key={p._id} value={p._id}>{p.name}</option>
                                        ))}
                                    </select>
                                    <ChevronDown className="w-4 h-4 text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                                </div>
                            )}
                            <button
                                onClick={() => setShowAddProjectModal(true)}
                                className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                            >
                                <Plus className="w-4 h-4" />
                                Add
                            </button>
                        </div>
                        {/* Tab Switcher */}
                        <div className="flex bg-gray-100 rounded-lg p-1">
                            <button
                                onClick={() => setActiveTab('banners')}
                                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${activeTab === 'banners' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                            >
                                Banners
                            </button>
                            <button
                                onClick={() => setActiveTab('sections')}
                                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${activeTab === 'sections' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                            >
                                Sections
                            </button>
                            <button
                                onClick={() => setActiveTab('settings')}
                                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${activeTab === 'settings' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                            >
                                App Settings
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Banners Tab */}
            {activeTab === 'banners' && (
                !selectedProject ? (
                    <div className="p-6">
                        <div className="text-center py-20 bg-white rounded-xl border">
                            <FolderOpen className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                            <p className="text-gray-500 font-medium">No project selected</p>
                            <p className="text-sm text-gray-400 mt-1">Please select a project or create one to manage banners</p>
                            <button
                                onClick={() => setShowAddProjectModal(true)}
                                className="mt-4 inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-medium hover:bg-blue-700"
                            >
                                <Plus className="w-4 h-4" />
                                Create Your First Project
                            </button>
                        </div>
                    </div>
                ) : (
                <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-2">
                            <Image className="w-5 h-5 text-gray-500" />
                            <h2 className="text-lg font-semibold text-gray-800">App Banners</h2>
                            <span className="text-sm text-gray-400">({banners.length})</span>
                        </div>
                        <button
                            onClick={handleAddBanner}
                            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                        >
                            <Plus className="w-4 h-4" />
                            Add Banner
                        </button>
                    </div>

                    {bannersLoading ? (
                        <div className="flex items-center justify-center py-20">
                            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                        </div>
                    ) : banners.length === 0 ? (
                        <div className="text-center py-20 bg-white rounded-xl border">
                            <Image className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                            <p className="text-gray-500 font-medium">No banners yet</p>
                            <p className="text-sm text-gray-400 mt-1">Add banners to show at the top of the mobile app</p>
                            <button
                                onClick={handleAddBanner}
                                className="mt-4 inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-medium hover:bg-blue-700"
                            >
                                <Plus className="w-4 h-4" />
                                Add Your First Banner
                            </button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {banners.map((banner) => (
                                <div key={banner._id} className="bg-white rounded-xl border overflow-hidden hover:shadow-md transition-shadow">
                                    <div className="relative">
                                        <img src={banner.imageUrl} alt={banner.title}
                                            className="w-full h-48 object-cover"
                                            onError={(e) => { e.target.src = '' }}
                                        />
                                        <div className="absolute top-2 right-2 flex gap-2">
                                            <button
                                                onClick={() => handleEditBanner(banner)}
                                                className="p-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 shadow-lg"
                                            >
                                                <Pencil className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteBanner(banner)}
                                                className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600 shadow-lg"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                        <div className="absolute bottom-2 left-2 bg-black/60 text-white px-2 py-0.5 rounded text-xs">
                                            Order: {banner.order}
                                        </div>
                                        {banner.gender && banner.gender !== 'all' && (
                                            <div className={`absolute bottom-2 right-2 px-2 py-0.5 rounded text-xs font-medium ${banner.gender === 'male' ? 'bg-blue-500 text-white' : 'bg-pink-500 text-white'}`}>
                                                {banner.gender}
                                            </div>
                                        )}
                                    </div>
                                    <div className="p-4">
                                        <h3 className="font-semibold text-gray-900">{banner.title}</h3>
                                        {banner.description && (
                                            <p className="text-sm text-gray-500 mt-1 line-clamp-2">{banner.description}</p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                )
            )}

            {/* Sections Tab */}
            {activeTab === 'sections' && (
                selectedProject ? (
                    <SectionsManager projectId={selectedProject._id} />
                ) : (
                    <div className="p-6">
                        <div className="text-center py-20 bg-white rounded-xl border">
                            <FolderOpen className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                            <p className="text-gray-500 font-medium">No project selected</p>
                            <p className="text-sm text-gray-400 mt-1">Please select a project or create one to manage sections</p>
                            <button
                                onClick={() => setShowAddProjectModal(true)}
                                className="mt-4 inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-medium hover:bg-blue-700"
                            >
                                <Plus className="w-4 h-4" />
                                Create Your First Project
                            </button>
                        </div>
                    </div>
                )
            )}

            {/* App Settings Tab */}
            {activeTab === 'settings' && (
                !selectedProject ? (
                    <div className="p-6">
                        <div className="text-center py-20 bg-white rounded-xl border">
                            <FolderOpen className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                            <p className="text-gray-500 font-medium">No project selected</p>
                            <p className="text-sm text-gray-400 mt-1">Please select a project or create one to manage app settings</p>
                            <button
                                onClick={() => setShowAddProjectModal(true)}
                                className="mt-4 inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-medium hover:bg-blue-700"
                            >
                                <Plus className="w-4 h-4" />
                                Create Your First Project
                            </button>
                        </div>
                    </div>
                ) : (
                <div className="p-6">
                    <div className="flex items-center gap-2 mb-6">
                        <Settings className="w-5 h-5 text-gray-500" />
                        <h2 className="text-lg font-semibold text-gray-800">App Configuration</h2>
                    </div>

                    {configLoading ? (
                        <div className="flex items-center justify-center py-20">
                            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                        </div>
                    ) : !appConfig ? (
                        <div className="text-center py-20 bg-white rounded-xl border">
                            <Settings className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                            <p className="text-gray-500 font-medium">No configuration found</p>
                        </div>
                    ) : (
                        <div className="space-y-4 max-w-2xl">
                            {/* Maintenance Mode Card */}
                            <div className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow">
                                <div className="flex items-start justify-between">
                                    <div className="flex items-start gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                                            <Settings className="w-5 h-5 text-red-600" />
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-gray-900">Maintenance Mode</h3>
                                            <p className="text-sm text-gray-500 mt-1">Show maintenance screen to app users</p>
                                        </div>
                                    </div>
                                    <div
                                        className={`relative inline-block w-11 h-6 rounded-full transition-colors ${appConfig?.maintenanceMode?.enabled ? 'bg-red-600' : 'bg-gray-200'} ${isSavingConfig ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                        onClick={() => !isSavingConfig && handleConfigUpdate('maintenanceMode.enabled', !appConfig?.maintenanceMode?.enabled)}
                                    >
                                        <span className={`absolute left-0.5 top-0.5 bg-white w-5 h-5 rounded-full shadow transform transition-transform ${appConfig?.maintenanceMode?.enabled ? 'translate-x-5' : 'translate-x-0'}`} />
                                    </div>
                                </div>
                                {appConfig?.maintenanceMode?.enabled && (
                                    <div className="mt-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Maintenance Message</label>
                                        <textarea
                                            value={getConfigValue('maintenanceMode.message') ?? ''}
                                            onChange={(e) => {
                                                setDraftConfig(prev => ({
                                                    ...(prev || {}),
                                                    maintenanceMode: {
                                                        ...(appConfig?.maintenanceMode || {}),
                                                        ...(prev?.maintenanceMode || {}),
                                                        message: e.target.value
                                                    }
                                                }))
                                            }}
                                            disabled={isSavingConfig}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none resize-none disabled:bg-gray-50 disabled:cursor-not-allowed"
                                            rows={3}
                                            placeholder="Enter message to show users during maintenance..."
                                        />
                                        <div className="mt-2 p-3 bg-red-50 rounded-lg border border-red-200">
                                            <p className="text-xs text-red-700 font-medium">App is currently in maintenance mode. Users will see the maintenance screen.</p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Save button for draft message */}
                            {draftConfig && (
                                <button
                                    onClick={handleSaveDraftConfig}
                                    disabled={isSavingConfig}
                                    className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-white font-semibold text-sm transition-all disabled:opacity-60 disabled:cursor-not-allowed hover:opacity-90 bg-indigo-600"
                                >
                                    {isSavingConfig ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Saving...
                                        </>
                                    ) : (
                                        'Save Configuration'
                                    )}
                                </button>
                            )}
                        </div>
                    )}
                </div>
                )
            )}

            {/* Add Banner Modal */}
            {showAddBannerModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between rounded-t-xl">
                            <h2 className="text-lg font-semibold text-gray-900">Add New Banner</h2>
                            <button onClick={() => { setShowAddBannerModal(false); dispatch(setApiJson(null)) }}>
                                <X className="w-5 h-5 text-gray-400 hover:text-gray-600" />
                            </button>
                        </div>
                        <form onSubmit={handleBannerSubmit} className="p-6 space-y-5">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                                <input
                                    type="text"
                                    value={ApiReducer?.apiJson?.title || ''}
                                    onChange={(e) => handleBannerInputChange('title', e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                                    placeholder="Enter banner title"
                                    required
                                />
                            </div>
                            <FileuploadComp title="Banner Image *" name="imageUrl" allowed={["img"]} />
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Show for Gender</label>
                                <div className="flex gap-2">
                                    {['all', 'male', 'female'].map((g) => (
                                        <button
                                            key={g}
                                            type="button"
                                            onClick={() => handleBannerInputChange('gender', g)}
                                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                                (ApiReducer?.apiJson?.gender || 'all') === g
                                                    ? g === 'male' ? 'bg-blue-600 text-white'
                                                        : g === 'female' ? 'bg-pink-600 text-white'
                                                            : 'bg-gray-800 text-white'
                                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            }`}
                                        >
                                            {g.charAt(0).toUpperCase() + g.slice(1)}
                                        </button>
                                    ))}
                                </div>
                                <p className="text-xs text-gray-400 mt-1">Choose who should see this banner</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Order</label>
                                <input
                                    type="number"
                                    value={ApiReducer?.apiJson?.order ?? 0}
                                    onChange={(e) => handleBannerInputChange('order', e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                                />
                                <p className="text-xs text-gray-400 mt-1">Lower numbers appear first</p>
                            </div>
                            <div className="flex gap-3 pt-2">
                                <button type="submit" disabled={isBannerSubmitting}
                                    className="flex-1 bg-blue-600 text-white px-6 py-2.5 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm font-medium">
                                    {isBannerSubmitting ? 'Adding...' : 'Add Banner'}
                                </button>
                                <button type="button" onClick={() => { setShowAddBannerModal(false); dispatch(setApiJson(null)) }}
                                    className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium">
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Banner Modal */}
            {showEditBannerModal && editingBanner && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between rounded-t-xl">
                            <h2 className="text-lg font-semibold text-gray-900">Edit Banner</h2>
                            <button onClick={() => { setShowEditBannerModal(false); setEditingBanner(null); dispatch(setApiJson(null)) }}>
                                <X className="w-5 h-5 text-gray-400 hover:text-gray-600" />
                            </button>
                        </div>
                        <form onSubmit={handleBannerUpdate} className="p-6 space-y-5">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Current Image</label>
                                <img src={editingBanner.imageUrl} alt={editingBanner.title} className="w-full h-40 object-cover rounded-lg border" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                                <input
                                    type="text"
                                    value={ApiReducer?.apiJson?.title || ''}
                                    onChange={(e) => handleBannerInputChange('title', e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                                    placeholder="Enter banner title"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Show for Gender</label>
                                <div className="flex gap-2">
                                    {['all', 'male', 'female'].map((g) => (
                                        <button
                                            key={g}
                                            type="button"
                                            onClick={() => handleBannerInputChange('gender', g)}
                                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                                (ApiReducer?.apiJson?.gender || 'all') === g
                                                    ? g === 'male' ? 'bg-blue-600 text-white'
                                                        : g === 'female' ? 'bg-pink-600 text-white'
                                                            : 'bg-gray-800 text-white'
                                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            }`}
                                        >
                                            {g.charAt(0).toUpperCase() + g.slice(1)}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Order</label>
                                <input
                                    type="number"
                                    value={ApiReducer?.apiJson?.order ?? 0}
                                    onChange={(e) => handleBannerInputChange('order', e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                                />
                                <p className="text-xs text-gray-400 mt-1">Lower numbers appear first</p>
                            </div>
                            <div className="flex gap-3 pt-2">
                                <button type="submit" disabled={isBannerSubmitting}
                                    className="flex-1 bg-blue-600 text-white px-6 py-2.5 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm font-medium">
                                    {isBannerSubmitting ? 'Updating...' : 'Update Banner'}
                                </button>
                                <button type="button" onClick={() => { setShowEditBannerModal(false); setEditingBanner(null); dispatch(setApiJson(null)) }}
                                    className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium">
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Add Project Modal */}
            {showAddProjectModal && (
                <AddProjectModal
                    unitId={unitId}
                    onClose={() => setShowAddProjectModal(false)}
                    onSuccess={handleProjectCreated}
                />
            )}
        </div>
    )
}

export default ClientWebApp
